import React from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    Card,
    CardContent,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryHeader } from '@/styles/typography';
import {
    CenteredSpinner,
    CycleCountBreadcrumbs,
    DetailHeaderBar,
    DetailLine,
    DetailNotFound,
    StatusChip,
} from '@/components/CycleCounts/blocks';
import {
    formatCycleCountDate,
    formatCycleCountDateTime,
    getCycleCountTypeLabel,
} from '@/components/CycleCounts/utils';
import { useCycleCountDetails } from './hooks';

const CycleCount = () => {
    const navigate = useNavigate();
    const { cycle_count_id: cycleCountId } = useParams();
    const { cycleCount, itemCountsByLocation, loading } = useCycleCountDetails(cycleCountId);

    if (loading && !cycleCount) {
        return <CenteredSpinner />;
    }

    if (!cycleCount) {
        return (
            <DetailNotFound
                title="Cycle Count Not Found"
                message="This cycle count could not be loaded."
            />
        );
    }

    const warehouseName = cycleCount.warehouse?.location_name || 'Cycle Count';
    const countLocations = cycleCount.count_locations || [];

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <DetailHeaderBar container direction="row">
                <CycleCountBreadcrumbs
                    trail={[
                        { label: 'Cycle Counts', to: '/cycle-counts' },
                        { label: warehouseName },
                    ]}
                />
            </DetailHeaderBar>

            <Grid
                container
                direction="column"
                css={css`
                    padding: 2rem;
                    gap: 1.5rem;
                    flex-wrap: nowrap;
                    overflow-y: auto;
                `}
            >
                <PrimaryHeader>{warehouseName}</PrimaryHeader>
                <Card>
                    <CardContent>
                        <DetailLine label="Date" value={formatCycleCountDate(cycleCount.date)} />
                        <DetailLine label="Type" value={getCycleCountTypeLabel(cycleCount)} />
                        <DetailLine
                            label="Assigned To"
                            value={cycleCount.assigned_teammate?.username}
                        />
                        <DetailLine
                            label="Status"
                            value={<StatusChip status={cycleCount.status} />}
                        />
                        <DetailLine
                            label="Time Started"
                            value={formatCycleCountDateTime(cycleCount.started_at)}
                        />
                        <DetailLine
                            label="Time Complete"
                            value={formatCycleCountDateTime(cycleCount.completed_at)}
                        />
                    </CardContent>
                </Card>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Location</TableCell>
                                <TableCell>Items</TableCell>
                                <TableCell align="right" style={{ width: 60 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {countLocations.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography color="textSecondary">
                                            No locations assigned to this cycle count.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                countLocations.map((location) => {
                                    const itemCount =
                                        itemCountsByLocation.get(location.warehouse_location) || 0;
                                    const audited = Boolean(location.audited_at);
                                    return (
                                        <TableRow
                                            key={location.cycle_count_location_id}
                                            hover
                                            css={css`cursor: pointer;`}
                                            onClick={() =>
                                                navigate(
                                                    `/cycle-counts/${cycleCountId}/locations/${location.cycle_count_location_id}`
                                                )
                                            }
                                        >
                                            <TableCell>{location.warehouse_location}</TableCell>
                                            <TableCell>{itemCount}</TableCell>
                                            <TableCell align="right">
                                                {audited ? (
                                                    <CheckCircleIcon
                                                        style={{ color: colors.greens.primary }}
                                                        aria-label="Audited"
                                                    />
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    );
};

export default CycleCount;
